import styled from '@emotion/styled';
import {Container} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {Slider} from 'UI/components/action/Slider';
import {BlockIntro} from 'UI/components/layout/BlockIntro';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';
import {NewsCard} from 'UI/components/module/newsBlock/NewsCard';

import {FadeInAnimation} from 'UI/animations/shared';

import {useNewsBlock} from './useNewsBlock';
import {isDefinedAndPopulated} from 'Application/utils/typeGuards';

import type {NewsBlockType} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `NewsBlock` component.
 * This interface specifies all the required and optional properties needed to configure a news block display.
 * It includes configuration for auto-filling articles, manually selected articles, block identification, and introductory content.
 */
interface ComponentProps {
    /**
     * Determines whether to automatically fill news articles or use manually selected ones.
     * When true, the component will use newsArticlesAuto instead of newsArticles for displaying content.
     */
    autoFill: NewsBlockType['autoFill'];
    /**
     * The unique identifier for this news block instance.
     * This ID is used for block intro linking and component identification within the page structure.
     */
    id: NewsBlockType['id'];
    /**
     * The introductory content configuration for the news block.
     * Contains the title, description, and other intro-related settings that appear above the news slider.
     */
    intro: NewsBlockType['intro'];
    /**
     * Manually selected news articles to display in the block.
     * This array contains the specific news articles chosen by content editors when autoFill is disabled.
     */
    newsArticles: NewsBlockType['newsArticles'];
    /**
     * Automatically selected news articles based on defined criteria.
     * This array is populated by the system when autoFill is enabled, containing articles that match specific rules or filters.
     */
    newsArticlesAuto: NewsBlockType['newsArticlesAuto'];
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing.
     *
     * @default 'NewsBlock'
     */
    testId?: string;
}

/**
 * The `NewsBlock` component renders a block of news articles in a slider format with an optional intro section.
 * It displays news articles either from manually selected articles or auto-filled articles based on the configuration.
 * The component uses a responsive slider to showcase news cards with different layouts for various screen sizes.
 *
 * @param props                  The component props.
 * @param props.autoFill         Determines whether to automatically fill news articles or use manually selected ones.
 * @param props.id               The unique identifier for this news block, used for block intro linking.
 * @param props.intro            The introductory content configuration for the block including title and description.
 * @param props.newsArticles     Manually selected news articles to display in the block.
 * @param props.newsArticlesAuto Automatically selected news articles based on criteria when autoFill is enabled.
 * @param props.testId           A unique identifier for testing purposes to locate this component during automated tests.
 * @returns A React element representing the news block with slider containing news cards.
 *
 * @example
 * ```tsx
 * <NewsBlock
 *     autoFill={false}
 *     id="news-block-1"
 *     intro={{ title: "Latest News", description: "Stay updated with our news" }}
 *     newsArticles={[article1, article2, article3]}
 *     newsArticlesAuto={[]}
 *     testId="latest-news-block"
 * />
 * ```
 */
const NewsBlock = observer(({
    autoFill,
    id,
    intro,
    newsArticles,
    newsArticlesAuto,
    testId = 'NewsBlock'
}: ComponentProps) => {
    const {news} = useNewsBlock(newsArticles, newsArticlesAuto, autoFill);

    return (
        <BlockWrapper>
            <Container>
                <BlockIntro blockId={id} intro={intro} testId={testId} />
                <ViewportTrigger baseComponent="div">
                    <motion.div variants={FadeInAnimation}>
                        <Slider slideCount={{lg: 3, sm: 2, xs: 1}}>
                            {news?.map(article => {
                                if (isDefinedAndPopulated(article)) {
                                    return (
                                        <NewsCard
                                            key={article.id}
                                            date={article.createdAt}
                                            headline={article.listView.headline}
                                            id={article.id}
                                            listDescription={article.listView.listDescription}
                                            listImage={article.listView.listImage}
                                            slug={article.slug}
                                            testId={`News-${article.id}`}
                                        />
                                    );
                                }

                                return null;
                            })}
                        </Slider>
                    </motion.div>
                </ViewportTrigger>
            </Container>
        </BlockWrapper>
    );
});

NewsBlock.displayName = 'NewsBlock';

export {NewsBlock};

const BlockWrapper = styled.div``;